/**
 * AI level generation — "generate a whole course/floor from the design", dispatched by the
 * game's 3Cs archetype (autorunner-2d track | topdown-2d floor plan). Both paths run the
 * VALIDATE-AND-REPAIR loop: parse through the real crucible-levels schema, run the
 * solvability validator, feed exact findings back to the model until it's beatable (or
 * best-effort after a few tries). Generated levels are edited/validated through the SAME
 * surface as hand-authored ones (unified create-or-generate).
 */
import type { DesignProject, LevelEntry } from '@volley/crucible-ipc-types';
import type { CrucibleClient } from '@volley/crucible-client';
import { z } from 'zod';
import {
  SpatialLevelSchema,
  spatialLevelKind,
  TopdownLevelSchema,
  topdownLevelKind,
  DataTableLevelSchema,
  dataTableLevelKind,
  BoardGridLevelSchema,
  boardGridLevelKind,
  compileTraversalGrammar,
  compileTopdownGrammar,
  resolveKinds,
  type SpatialLevel,
  type TopdownLevel,
  type DataTableLevel,
  type BoardGridLevel,
  type ThreeCsProfile,
  type AutorunnerThreeCs,
  type TopdownThreeCs,
  type DataThreeCs,
  type LevelFinding,
} from '@volley/crucible-levels';
import { aiChat, readSection } from './anvil-api';
import { parseAiJson } from './schemas';
import {
  threeCsForKind,
  kindForArchetype,
  coerceEnabledKind,
  type LevelKindChoice,
} from './level-defaults';
import { readContentPalette } from './level-wizard';

const MAX_ATTEMPTS = 4;

let idCounter = 0;
function freshId(): string {
  idCounter += 1;
  return `level_${Date.now().toString(36)}_${idCounter}`;
}

function autorunnerGuide(threeCs: AutorunnerThreeCs, palette?: ReadonlySet<string>): string {
  const g = compileTraversalGrammar(threeCs);
  const c = threeCs.character;
  const tether = threeCs.tether;
  const playerDriven = threeCs.locomotion === 'player';
  const tetherRules = tether
    ? `
- reversal: clearWindowS must exceed (span + ${(g.maxGroupSpread + c.bodyWidth).toFixed(1)} m) /
  ${Math.abs(c.backpedalSpeed)} — i.e. keep reversal spans SHORT (a few metres) and windows generous.
- Prefer NOT to set requiresSimultaneous. If you do, that window must be >= ${(g.minGroupFootprint + 0.1).toFixed(1)} m
  AND preceded by a long platform so the ${tether.members}-player group can bunch.
- collapse: only where the group can outrun it; keep collapseSpeed <= ${(g.draggedGroupSpeed / 1.15).toFixed(1)} m/s.`
    : `
- reversal: clearWindowS must exceed (span + ${c.bodyWidth.toFixed(1)} m) / ${Math.abs(c.backpedalSpeed)}.
- collapse: keep collapseSpeed <= ${(g.draggedGroupSpeed / 1.15).toFixed(1)} m/s so it stays outrunnable.
- Do NOT set requiresSimultaneous (single body — it is meaningless here).`;
  const items =
    palette && palette.size > 0
      ? [...palette].sort().join(', ')
      : '(none listed — use ONLY names that appear verbatim in the Content section above)';
  const maxTerrace = (g.maxRise - 0.6).toFixed(1);
  const maxPickup = (g.maxRise - 0.1).toFixed(1);
  const arcApex = (g.maxRise * 0.75).toFixed(1);
  return `You are a level designer for a 2D SIDE-SCROLLER (${
    playerDriven
      ? 'player-driven movement — Mario-like pacing: reward exploration, allow breathers'
      : 'an AUTO-RUNNER — relentless left-to-right momentum'
  }). Design one course as a single left-to-right track. Segment kinds are physical
features: platform = solid ground; gap = a PIT (a hole in the floor the player falls into
if they don't jump it); fence = a solid obstacle standing on the ground, jumped over;
reversal/collapse = timed ground hazards. Output ONLY JSON matching this shape (no prose, no markdown):

{
  "archetype": "autorunner-2d",
  "targetSeconds": <number, 60-90>,
  "vertices": [ { "id": "<unique>", "t": <0..1 STRICTLY INCREASING>, "elevation": <metres> }, ... ],
  "segments": [ {
      "fromId": "<vertex id>", "toId": "<the NEXT vertex id>",
      "kind": "gap" | "fence" | "platform" | "reversal" | "collapse",
      "span": <metres, edge-to-edge, >= 0>,
      "obstacleHeight": <fence only>, "obstacleWidth": <fence only>,
      "clearWindowS": <reversal only, seconds>, "collapseSpeed": <collapse only, m/s>,
      "requiresSimultaneous": <optional boolean>
  }, ... ],
  "terraces": [ { "id": "<unique>", "tStart": <0..1>, "tEnd": <after tStart>, "height": <m above ground> }, ... ],
  "entities": [ { "id": "<unique>", "ref": "<EXACT Content name>", "role": "enemy"|"pickup"|"checkpoint"|"hazard"|"prop", "t": <0..1>, "elevation": <optional m ABOVE GROUND>, "hp": <enemy only, hits to defeat>, "movement": "static"|"patrol"|"chase", "patrolRange": <patrol only, m>, "patrolSec": <patrol only, one sweep>, "cycle": { "loopSec": <period>, "activeSec": <dangerous window>, "telegraphSec": <optional warn> } }, ... ]
}

AVAILABLE CONTENT ITEMS — every entity "ref" MUST be one of these EXACT names (never invent one):
${items}

THE OBJECT LAYER is what makes a course feel alive — author it richly, not sparsely:
- COINS (role "pickup"): lay a continuous TRAIL of coins ~1 m above the ground along the
  natural running line, and bend it into an ARC over every gap (elevation rising toward
  ${arcApex} m at the gap's midpoint, back down after) so the coins TRACE the ideal jump.
  Put the big challenge coin (the large pickup) at the apex over a DEEP gap or a tall fence.
- \`elevation\` on an entity is HEIGHT ABOVE THE LOCAL GROUND (m) — omit it to sit on the
  ground. Coins float along the line; enemies/hazards sit on the ground (omit elevation).
- ENEMIES / HAZARDS (role "enemy" / "hazard"): place the level's threats on the track and
  cluster them where the difficulty story wants a spike. Do NOT use a vertex "hazard" field —
  every threat is an entity so it stays editable. Give enemies BEHAVIOR: hp (hits to defeat;
  omit for one-hit/stomp), movement (static | patrol | chase), patrolRange (m, if patrol).
  For a CYCLIC hazard (retracting spikes, erupting geyser, toggling/vanishing block, a
  moving/swinging platform) add a "cycle" {loopSec, activeSec, telegraphSec?} — the previs
  animates it and the game plays it from the same numbers.
- TERRACES: an overhead one-way platform = an ALTERNATE HIGH ROUTE (Mario-style). Give 1-2
  per course, each with a coin reward on top so the climb pays off.

HARD CONSTRAINTS (from the 3Cs — the course is REJECTED unless every one holds):
- Every segment connects ADJACENT vertices in order (segment i: vertices[i] -> vertices[i+1]).
- Flat gaps span <= ${g.maxJumpGap.toFixed(1)} m; a gap that RISES needs to be much shorter.
- Fences: obstacleHeight < ${(g.maxRise - 0.1).toFixed(1)} m AND must be preceded by a
  platform of span >= ${(g.maxJumpGap / 2).toFixed(1)} m (run-up to center the jump).${tetherRules}
- Terrace height <= ${maxTerrace} m; any pickup elevation <= ${maxPickup} m above ground
  (a coin above the jump apex is uncatchable).
- Open and end on a platform; alternate platforms with hazards; escalate difficulty.
- Every entity has a "role" and a "ref" that is an EXACT name from the list above.`;
}

function topdownGuide(threeCs: TopdownThreeCs): string {
  const g = compileTopdownGrammar(threeCs);
  return `You are a level designer for a TOP-DOWN 2D action-adventure (StarTropics-like).
Design one dungeon/area as a floor plan of rooms and corridors. Output ONLY JSON matching
this shape (no prose, no markdown):

{
  "archetype": "topdown-2d",
  "targetSeconds": <number, 90-180>,
  "rooms": [ { "id": "<unique>", "x": <m>, "y": <m>, "w": <m>, "h": <m>,
               "kind": "start" | "goal" | "combat" | "puzzle" | "boss" | "normal",
               "hazard": <optional mechanic name>, "difficulty": <optional 0..1> }, ... ],
  "corridors": [ { "fromId": "<room>", "toId": "<room>", "width": <m>, "lockedBy": <optional key id> }, ... ],
  "keys": [ { "id": "<unique>", "roomId": "<room the key sits in>" }, ... ],
  "entities": [ { "id": "<unique>", "ref": "<EXACT Content item name — enemy/pickup/NPC/switch>", "roomId": "<room>", ...other typed fields }, ... ]
}

HARD CONSTRAINTS (from the 3Cs — the floor is REJECTED unless every one holds):
- Exactly ONE room with kind "start"; at least one "goal".
- Every corridor width >= ${g.minCorridorWidth.toFixed(1)} m; every room at least
  ${g.minRoomSide.toFixed(1)} m on each side.
- REACHABILITY: the goal must be reachable from the start. A locked corridor only opens once
  its key's room is reachable FIRST — never place a key behind the very door it opens.
- Rooms should mostly fit the camera (${g.viewW}×${g.viewH} m); lay rooms out on a rough
  grid with x/y so neighbours sit beside each other (no overlaps needed, corridors connect ids).
- 6-12 rooms; use 1-3 locked doors with keys placed earlier on the path; put combat/puzzle
  rooms between the start and goal; escalate difficulty toward the boss/goal.
- Populate rooms via "entities" with ref = EXACT Content item names (never invent
  referents) — combat rooms get their enemies, puzzle rooms their switches/props.`;
}

function dataGuide(threeCs: DataThreeCs): string {
  return `You are designing ONE data level for a property-driven game — a typed data blob the
game consumes directly (e.g. a match-3 starting board, a gem loot table, a grab-bag source).${
    threeCs.notes ? `\nThis game's data levels represent: ${threeCs.notes}` : ''
  }
Output ONLY JSON matching this shape (no prose, no markdown):

{
  "weightMode": "probability" | "relative"   (optional — "relative" for loot-style weights),
  "rows": [ { "id": "<unique>", "weight": <optional number>,
              "refs": [<optional exact Content item names this row points at>],
              ...whatever other TYPED fields this data needs }, ... ]
}

RULES:
- At least 1 row; every row id unique and meaningful (e.g. "ruby-gem", not "row1").
- Rows are TYPED data, not prose — numbers as numbers, booleans as booleans.
- Shape the extra fields to what the design actually needs: a starting board can be rows of
  {id, x, y, piece}; a loot table rows of {id, weight, refs}; a config set rows of {id, key, value}.
- Use "refs" with EXACT Content item names (e.g. "Fire Gem") so cross-references stay navigable.
- weightMode "probability": weights must sum to 1. "relative": any positive weights.`;
}

/** A level the design calls for that has no level entry yet. */
export interface DesignedLevel {
  name: string;
  summary: string;
  /** Concrete generation brief (enemy/difficulty/theme/mechanics this level loads). */
  brief: string;
  /** Tags stamped onto the created entry (cross-refs + build-agent routing). */
  tags?: string[];
  /** Per-level kind (design §17 palette) — a design can mix kinds. */
  kind?: LevelKindChoice;
}

const ManifestSchema = z.object({
  defined: z.boolean(),
  missing: z
    .array(
      z.object({
        name: z.string().min(1),
        summary: z.string(),
        brief: z.string(),
        tags: z.array(z.string()).max(6).optional(),
        kind: z.enum(['spatial-2d', 'topdown-2d', 'data-table', 'board-grid']).optional(),
      }),
    )
    .max(30),
});

/**
 * Reconcile the design against the existing level entries: does the design define a
 * concrete, enumerable level set (a 10-rung ladder, named worlds/stages), and which of
 * those levels have no entry yet? Empty ⇒ nothing enumerated is missing (the caller
 * falls back to pitching one level).
 */
export async function extractLevelManifest(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
    existing: LevelEntry[];
  },
): Promise<DesignedLevel[]> {
  const { gameId, project, existing } = args;
  const ordered = [...project.sections].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const s of ordered) {
    try {
      parts.push(`\n## ${s.title}\n${await readSection(client, gameId, s.filename)}`);
    } catch {
      /* skip unreadable */
    }
  }
  const existingIdx = existing.map((l) => ({ name: l.name, summary: l.summary ?? '' }));

  const resp = await aiChat(client, {
    gameId,
    system: `You reconcile a game design against its level entries. Decide whether the DESIGN
DEFINES a concrete, enumerable set of levels — e.g. a 10-rung ladder (one level per named
enemy), named worlds/stages/boards, an authored campaign. Output ONLY JSON (no prose):

{"defined": <boolean>, "missing": [ { "name": "<the level's name AS THE DESIGN NAMES IT>",
  "summary": "<one line>", "brief": "<concrete generation brief: which enemy/difficulty/theme/mechanics this level loads>",
  "tags": ["<1-4 lowercase tags: role in the design (e.g. ladder, level-3, boss) — build agents route on these>"],
  "kind": "spatial-2d" | "topdown-2d" | "data-table" | "board-grid" } ] }
KINDS (a design can mix them): spatial-2d = a side-scroll 2D track; topdown-2d = a top-down floor plan (rooms/corridors); data-table = a typed data set (loot table, config rows); board-grid = a paintable W×H tile board (match-3 starting boards, tactics maps). Choose per level what the design actually describes.

RULES:
- "defined": false when the design wants a procedural/endless/unenumerated level set — then "missing" MUST be [].
- List ONLY design-defined levels NOT already covered by an existing entry (match by MEANING — "Level 3" covers "Ladder rung 3"; a timestamped "Generated level ..." entry covers NOTHING).
- Keep the design's order (level 1 first). Name levels exactly as the design refers to them (e.g. "Level 4 — Kraken").
- Briefs must be concrete and per-level distinct: name the difficulty knobs/enemy/theme this specific level uses.`,
    messages: [
      {
        role: 'user',
        content: `Game: ${project.name}\n\nDesign:\n${parts.join('\n')}\n\n# Existing level entries\n${JSON.stringify(existingIdx)}\n\nReconcile now. Output ONLY the JSON.`,
      },
    ],
    maxTokens: 4096,
  });
  const manifest = parseAiJson(resp.text, ManifestSchema);
  if (!manifest.defined) return [];
  // Belt-and-braces: drop anything already covered by an existing entry — matched on the
  // entry's CURRENT name OR its design-origin name (design §18), so a level whose entry was
  // renamed still de-dupes against the unchanged design name instead of spawning a twin.
  const have = new Set<string>();
  for (const l of existing) {
    have.add(l.name.trim().toLowerCase());
    if (l.source?.designName) have.add(l.source.designName.trim().toLowerCase());
  }
  return manifest.missing.filter((m) => !have.has(m.name.trim().toLowerCase()));
}

/** Judge reply → 3Cs archetype ('other' / unparseable → null: keep the current default). */
export function archetypeFromJudgeReply(
  text: string,
): 'autorunner-2d' | 'topdown-2d' | 'data-table' | null {
  try {
    const data = parseAiJson(
      text,
      z.object({ perspective: z.enum(['side-scroll', 'top-down', 'data', 'other']) }),
    );
    if (data.perspective === 'side-scroll') return 'autorunner-2d';
    if (data.perspective === 'top-down') return 'topdown-2d';
    if (data.perspective === 'data') return 'data-table';
    return null;
  } catch {
    return null;
  }
}

/**
 * Derive the 3Cs archetype for a game with no authored three-cs.json, so a
 * StarTropics-like doesn't default to the side-scroll autorunner (regex-free per the
 * semantic-classification rule): a structured genre that maps deterministically wins;
 * otherwise ONE small LLM-judge call over the pitch + leading sections.
 */
export async function deriveThreeCsArchetype(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
  },
): Promise<'autorunner-2d' | 'topdown-2d' | 'data-table' | null> {
  const { gameId, project } = args;

  // Structured field first: a genre the deterministic kind map already knows.
  const kinds = resolveKinds({ genre: project.profile?.genre });
  if (kinds.includes('topdown-2d')) return 'topdown-2d';
  if (kinds.includes('spatial-2d')) return 'autorunner-2d';
  if (kinds.includes('data-table')) return 'data-table';
  // (Weekend feel archetypes like "twitch" don't map — fall through to the judge.)

  const ordered = [...project.sections].sort((a, b) => a.order - b.order).slice(0, 2);
  const parts: string[] = [];
  for (const s of ordered) {
    try {
      parts.push(
        `\n## ${s.title}\n${(await readSection(client, gameId, s.filename)).slice(0, 4000)}`,
      );
    } catch {
      /* skip unreadable */
    }
  }

  try {
    const resp = await aiChat(client, {
      gameId,
      system: `You classify a game's level-space perspective for a level editor. Output ONLY JSON:
{"perspective": "side-scroll" | "top-down" | "data" | "other"}
- "side-scroll": levels are a left-to-right 2D track (runner, platformer).
- "top-down": levels are a 2D floor plan seen from above (Zelda/StarTropics-like action-adventure, dungeon crawl, top-down shooter).
- "data": levels are property/data sets, not spatial layouts — match-3 starting boards, loot tables, trivia/question packs, grab-bag sources.
- "other": none of the above (e.g. free-form 3D spaces).`,
      messages: [
        {
          role: 'user',
          content: `Game: ${project.name}\nPitch: ${(project.pitch ?? '').slice(0, 1500)}\n${parts.join('\n')}\n\nClassify the perspective. Output ONLY the JSON.`,
        },
      ],
      maxTokens: 128,
    });
    return archetypeFromJudgeReply(resp.text);
  } catch {
    return null;
  }
}

/**
 * Compact levels index + progression handed to the model (design §16) — lets the AI (and
 * the build LLM) address levels/mechanics as DATA, not prose.
 */
export function levelsContextBlock(levels?: LevelEntry[], progression?: unknown): string {
  if ((!levels || levels.length === 0) && !progression) return '';
  const idx = (levels ?? []).map((l) => ({
    id: l.id,
    name: l.name,
    kind: l.kind,
    tags: l.tags ?? [],
    summary: l.summary ?? '',
  }));
  return `\n\n# Existing levels (index)\n${JSON.stringify(idx)}\n# Progression\n${JSON.stringify(progression ?? null)}`;
}

function boardGuide(): string {
  return `You are designing ONE board level for a grid-based game — a paintable W×H tile board
(e.g. a match-3 starting board, a puzzle setup, a tactics map). Output ONLY JSON matching
this shape (no prose, no markdown):

{
  "width": <int, 1-64>, "height": <int, 1-64>,
  "palette": ["<tile names — EXACT Content item names where they exist>"],
  "cells": [ { "x": <0..width-1>, "y": <0..height-1>, "tile": "<palette entry>" }, ... ]
}

RULES:
- Cells are SPARSE — omit empty cells entirely. One tile per cell, all tiles from the palette.
- y grows DOWNWARD (y = height-1 is the bottom row) — pile board-fill from the bottom.
- Shape the board to the design's rules (e.g. a match-3 starting board must not contain
  pre-made matches; a tactics map places terrain + spawn markers).`;
}

interface ArchetypeBits {
  kindId: LevelKindChoice;
  guide: string;
  parse: (text: string) => unknown;
  errors: (data: unknown) => LevelFinding[];
}

/** Repair-loop finding filter: hard errors always; orphan-refs too (warn severity, but the
 *  loop can fix an invented referent — design §18 reference integrity). */
function repairable(f: LevelFinding): boolean {
  return f.severity === 'error' || f.code === 'orphan-ref';
}

/** Generation/validation bits per KIND (design §17 — kind is the per-level choice; the
 *  caller supplies a kind-appropriate 3Cs via threeCsForKind, and optionally the Content
 *  palette so ref integrity is checked + repaired). */
function bitsForKind(
  requestedKind: LevelKindChoice,
  threeCs: ThreeCsProfile,
  palette?: ReadonlySet<string>,
): ArchetypeBits {
  // Disabled kinds (topdown-2d / board-grid) aren't offered right now — coerce to the default
  // so generation produces a supported kind WITH matching data (design tab scope: side view +
  // data only). Re-enable via AVAILABLE_LEVEL_KINDS in level-defaults.ts.
  const kind = coerceEnabledKind(requestedKind);
  if (kind === 'topdown-2d' && threeCs.archetype === 'topdown-2d') {
    return {
      kindId: 'topdown-2d',
      guide: topdownGuide(threeCs),
      parse: (t) => parseAiJson(t, TopdownLevelSchema),
      errors: (d) =>
        topdownLevelKind.validate(d as TopdownLevel, { threeCs, palette }).filter(repairable),
    };
  }
  if (kind === 'data-table') {
    return {
      kindId: 'data-table',
      guide: dataGuide(threeCs.archetype === 'data-table' ? threeCs : { archetype: 'data-table' }),
      parse: (t) => parseAiJson(t, DataTableLevelSchema),
      errors: (d) =>
        dataTableLevelKind.validate(d as DataTableLevel, { palette }).filter(repairable),
    };
  }
  if (kind === 'board-grid') {
    return {
      kindId: 'board-grid',
      guide: boardGuide(),
      parse: (t) => parseAiJson(t, BoardGridLevelSchema),
      errors: (d) =>
        boardGridLevelKind.validate(d as BoardGridLevel, { palette }).filter(repairable),
    };
  }
  if (threeCs.archetype !== 'autorunner-2d') {
    // threeCsForKind should make this unreachable; fail loudly rather than mis-generate.
    throw new Error(`no ${threeCs.archetype} 3Cs available for a ${kind} level`);
  }
  return {
    kindId: 'spatial-2d',
    guide: autorunnerGuide(threeCs, palette),
    parse: (t) => parseAiJson(t, SpatialLevelSchema),
    errors: (d) =>
      spatialLevelKind.validate(d as SpatialLevel, { threeCs, palette }).filter(repairable),
  };
}

export interface GenerateLevelArgs {
  gameId: string;
  project: DesignProject;
  threeCs: ThreeCsProfile;
  /** Explicit level kind (design §17 palette) — defaults to the archetype's kind.
   *  A kind that differs from the archetype generates against that kind's default 3Cs. */
  kind?: LevelKindChoice;
  /** Persist a level (the useLevels hook's `save`); keeps the list in sync. */
  save: (level: LevelEntry) => Promise<LevelEntry>;
  /** Optional designer brief from the new-level wizard ("Make a fire level!"). */
  brief?: string;
  /** Optional accepted pitch: name + mechanics to feature (tagged via `hazard` fields). */
  pitch?: {
    name: string;
    summary: string;
    mechanics: { name: string; description: string }[];
    /** Stamped onto the created entry (cross-refs + build-agent routing). */
    tags?: string[];
  };
  /** Existing levels + progression — context so the AI slots the new level coherently. */
  levels?: LevelEntry[];
  progression?: unknown;
  /** Progression slot this level is being made for ("level 3 of 5", difficulty hint). */
  slot?: { label: string; index: number; total: number; difficultyHint?: string };
  /** Design origin (design §18) — stamped onto the entry so a regenerate re-matches by
   *  origin, not just current name. Set when creating from the design manifest. */
  source?: { sectionId: string; designName: string };
}

export interface GenerateLevelResult {
  level: LevelEntry;
  /** Residual validator errors (empty ⇒ solvable). */
  findings: LevelFinding[];
  attempts: number;
}

export async function generateLevelFromDesign(
  client: CrucibleClient,
  args: GenerateLevelArgs,
): Promise<GenerateLevelResult> {
  const { gameId, project, save, brief, pitch, levels, progression, slot, source } = args;
  const kind = args.kind ?? kindForArchetype(args.threeCs.archetype);
  const threeCs = threeCsForKind(kind, args.threeCs);
  const palette = new Set((await readContentPalette(client, gameId, project)).map((p) => p.name));
  const bits = bitsForKind(kind, threeCs, palette);

  // Design context — all section bodies in order.
  const ordered = [...project.sections].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const s of ordered) {
    try {
      parts.push(`\n## ${s.title}\n${await readSection(client, gameId, s.filename)}`);
    } catch {
      /* skip unreadable */
    }
  }

  const themeBlock = [
    brief ? `\nDesigner's request: ${brief}` : '',
    pitch
      ? `\nAccepted pitch — build THIS level: "${pitch.name}" — ${pitch.summary}${
          pitch.mechanics.length > 0
            ? `\nFeature these mechanics — place each as an entity (role "enemy"/"hazard") at the point on the track where it's used:\n${pitch.mechanics.map((m) => `- ${m.name}: ${m.description}`).join('\n')}`
            : ''
        }`
      : '',
    slot
      ? `\nThis will be level ${slot.label} (${slot.index + 1} of ${slot.total}) in the campaign — pitch its difficulty accordingly${slot.difficultyHint ? `: ${slot.difficultyHint}` : '.'}`
      : '',
  ].join('');
  const baseUser = `Game: ${project.name}\n\nDesign:\n${parts.join('\n')}${levelsContextBlock(levels, progression)}${themeBlock}\n\nDesign one level now. Output ONLY the JSON.`;

  let best: { data: unknown; errs: LevelFinding[] } | null = null;
  let attempts = 0;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    attempts = attempt;
    let user = baseUser;
    if (best) {
      const list = best.errs
        .map(
          (f) =>
            `- ${f.code}${f.at?.vertexId ? ` @${f.at.vertexId}` : f.at?.roomId ? ` @${f.at.roomId}` : ''}: ${f.message}`,
        )
        .join('\n');
      user = `${baseUser}\n\nYour previous attempt was NOT beatable. Fix EXACTLY these ${best.errs.length} issue(s), keep everything else intact:\n${list}\n\nPrevious JSON:\n${JSON.stringify(best.data)}\n\nReturn ONLY the corrected full JSON.`;
    }

    const resp = await aiChat(client, {
      gameId,
      system: bits.guide,
      messages: [{ role: 'user', content: user }],
      maxTokens: 8192,
    });

    let data: unknown;
    try {
      data = bits.parse(resp.text);
    } catch {
      continue; // malformed JSON/schema — try again (best unchanged)
    }

    const errs = bits.errors(data);
    if (errs.length === 0) {
      best = { data, errs };
      break;
    }
    if (!best || errs.length < best.errs.length) best = { data, errs };
  }

  if (!best)
    throw new Error(`Generation failed to produce valid level JSON after ${attempts} attempts.`);

  const solvable = best.errs.length === 0;
  const baseName = pitch?.name ?? `Generated level ${new Date().toLocaleTimeString()}`;
  const level: LevelEntry = {
    id: freshId(),
    name: solvable ? baseName : `${baseName} (needs fixes)`,
    kind: bits.kindId,
    ...(pitch?.tags && pitch.tags.length > 0 ? { tags: pitch.tags } : {}),
    ...(source ? { source } : {}),
    summary: pitch?.summary,
    data: best.data,
    createdAt: '',
    updatedAt: '',
  };
  const saved = await save(level);
  return { level: saved, findings: best.errs, attempts };
}

export interface FixLevelResult {
  data: unknown;
  findings: LevelFinding[];
  attempts: number;
}

/**
 * "Reimagine with context" — directed AI editing of an existing level (design §16): the
 * current JSON + a freeform instruction + the full design + levels/progression context +
 * this level's campaign slot, through the validate-and-repair loop. Undoable by the caller.
 */
export async function reimagineLevelWithAI(
  client: CrucibleClient,
  args: {
    gameId: string;
    project: DesignProject;
    threeCs: ThreeCsProfile;
    /** The level's kind (design §17) — defaults to the profile archetype's kind. */
    kind?: LevelKindChoice;
    data: unknown;
    instruction: string;
    levels?: LevelEntry[];
    progression?: unknown;
    slot?: { label: string; index: number; total: number };
  },
): Promise<FixLevelResult> {
  const { gameId, project, threeCs, data, instruction, levels, progression, slot } = args;
  const palette = new Set((await readContentPalette(client, gameId, project)).map((p) => p.name));
  const bits = bitsForKind(args.kind ?? kindForArchetype(threeCs.archetype), threeCs, palette);

  const ordered = [...project.sections].sort((a, b) => a.order - b.order);
  const parts: string[] = [];
  for (const s of ordered) {
    try {
      parts.push(`\n## ${s.title}\n${await readSection(client, gameId, s.filename)}`);
    } catch {
      /* skip unreadable */
    }
  }
  const slotLine = slot
    ? `\nThis is level ${slot.label} (${slot.index + 1} of ${slot.total}) in the campaign — keep its difficulty consistent with that position.`
    : '';
  const base = `Game design:\n${parts.join('\n')}${levelsContextBlock(levels, progression)}${slotLine}\n\nCurrent level JSON:\n${JSON.stringify(data)}\n\nThe designer wants: ${instruction}\n\nReimagine the level accordingly — keep what still fits, change what's asked, and keep it BEATABLE per the hard constraints. Return ONLY the full corrected JSON.`;

  let best: { data: unknown; errs: LevelFinding[] } | null = null;
  let attempts = 0;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    attempts = attempt;
    let user = base;
    if (best && best.errs.length > 0) {
      const list = best.errs
        .map(
          (f) =>
            `- ${f.code}${f.at?.vertexId ? ` @${f.at.vertexId}` : f.at?.roomId ? ` @${f.at.roomId}` : ''}: ${f.message}`,
        )
        .join('\n');
      user = `${base}\n\nYour previous attempt was NOT beatable. Fix EXACTLY these ${best.errs.length} issue(s), keep everything else intact:\n${list}\n\nPrevious JSON:\n${JSON.stringify(best.data)}\n\nReturn ONLY the corrected full JSON.`;
    }
    const resp = await aiChat(client, {
      gameId,
      system: bits.guide,
      messages: [{ role: 'user', content: user }],
      maxTokens: 8192,
    });
    let d: unknown;
    try {
      d = bits.parse(resp.text);
    } catch {
      continue;
    }
    const errs = bits.errors(d);
    if (!best || errs.length <= best.errs.length) best = { data: d, errs };
    if (errs.length === 0) break;
  }
  if (!best) throw new Error(`Reimagine produced no valid level JSON after ${attempts} attempts.`);
  return { data: best.data, findings: best.errs, attempts };
}

/**
 * "Fix with AI" — repair an EXISTING level in place. Starts from the current data + its
 * findings and iterates the model to clear them (keeping the rest), returning the best result.
 */
export async function fixLevelWithAI(
  client: CrucibleClient,
  args: {
    gameId: string;
    threeCs: ThreeCsProfile;
    /** The level's kind (design §17) — defaults to the profile archetype's kind. */
    kind?: LevelKindChoice;
    /** Content palette for ref-integrity repair (design §18). */
    palette?: ReadonlySet<string>;
    data: unknown;
  },
): Promise<FixLevelResult> {
  const { gameId, threeCs, data } = args;
  const bits = bitsForKind(args.kind ?? kindForArchetype(threeCs.archetype), threeCs, args.palette);
  let best = { data, errs: bits.errors(data) };
  let attempts = 0;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS && best.errs.length > 0; attempt++) {
    attempts = attempt;
    const list = best.errs
      .map(
        (f) =>
          `- ${f.code}${f.at?.vertexId ? ` @${f.at.vertexId}` : f.at?.roomId ? ` @${f.at.roomId}` : ''}: ${f.message}`,
      )
      .join('\n');
    const user = `This level is NOT beatable. Fix EXACTLY these ${best.errs.length} issue(s), keep everything else intact:\n${list}\n\nLevel JSON:\n${JSON.stringify(best.data)}\n\nReturn ONLY the corrected full JSON.`;
    const resp = await aiChat(client, {
      gameId,
      system: bits.guide,
      messages: [{ role: 'user', content: user }],
      maxTokens: 8192,
    });
    let d: unknown;
    try {
      d = bits.parse(resp.text);
    } catch {
      continue;
    }
    const errs = bits.errors(d);
    if (errs.length < best.errs.length) best = { data: d, errs };
    if (errs.length === 0) break;
  }
  return { data: best.data, findings: best.errs, attempts };
}
